import { useState } from "react";

import type { Administrator, PasswordReset } from "../api/types";
import { ErrorNotice } from "../components/ui";
import { ConfirmDialog } from "../products/ConfirmDialog";
import { useAccountAction, useResetPassword } from "./useUsers";

export type AccountAction = "disable" | "reactivate" | "reset";

const COPY: Record<
  AccountAction,
  { title: string; confirm: string; destructive: boolean; body: string }
> = {
  disable: {
    title: "Disable administrator",
    confirm: "Disable account",
    destructive: true,
    body:
      "They will be signed out of every session immediately and will not be " +
      "able to sign in again. Nothing they did is removed — the account stays " +
      "here, disabled, and everything it is recorded as having done stays " +
      "attributed to it.",
  },
  reactivate: {
    title: "Reactivate administrator",
    confirm: "Reactivate account",
    destructive: false,
    body:
      "They will be able to sign in again with the password they had. The " +
      "sessions that ended when the account was disabled are not restored — " +
      "signing in again is what proves they still hold the password.",
  },
  reset: {
    title: "Reset password",
    confirm: "Reset password",
    destructive: true,
    body:
      "A new temporary password is generated and shown to you once. Their " +
      "current password stops working immediately, every session ends, and " +
      "they must choose their own password the next time they sign in.",
  },
};

/**
 * Disable, reactivate, or issue a new temporary password.
 *
 * Three actions and one dialog, because they are the same shape: an
 * irreversible-feeling change to somebody else's account, confirmed with the
 * acting owner's own password, with the consequences stated before the button
 * rather than discovered after it. What differs is the sentence, so that is
 * what the table above holds.
 *
 * The password is typed here, sent in one request and kept nowhere.
 */
export function AccountActionDialog({
  administrator,
  action,
  onReset,
  onClose,
}: {
  administrator: Administrator;
  action: AccountAction;
  onReset: (result: PasswordReset) => void;
  onClose: () => void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const accountAction = useAccountAction();
  const resetPassword = useResetPassword();

  const copy = COPY[action];
  const busy = accountAction.isPending || resetPassword.isPending;
  const error = accountAction.error ?? resetPassword.error;
  const ready = currentPassword.length > 0;

  function confirm() {
    if (!ready) return;
    if (action === "reset") {
      resetPassword.mutate(
        { adminId: administrator.admin_id, currentPassword },
        { onSuccess: onReset },
      );
      return;
    }
    accountAction.mutate(
      { adminId: administrator.admin_id, action, currentPassword },
      { onSuccess: onClose },
    );
  }

  return (
    <ConfirmDialog
      title={copy.title}
      confirmLabel={copy.confirm}
      destructive={copy.destructive}
      busy={busy}
      confirmDisabled={!ready}
      onCancel={onClose}
      onConfirm={confirm}
    >
      <div className="stack" data-testid="account-action-dialog">
        <dl className="users__facts">
          <dt>Administrator</dt>
          <dd>{administrator.display_name}</dd>
          <dt>Email</dt>
          <dd>{administrator.email}</dd>
          <dt>Signed in now</dt>
          <dd>{administrator.active_session_count}</dd>
        </dl>

        <p>{copy.body}</p>

        <div className="users__field">
          <label className="users__label" htmlFor="action-confirm-password">
            Your password
          </label>
          <input
            id="action-confirm-password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
          <p className="users__hint">
            Your own password, to confirm this change.
          </p>
        </div>

        {error ? <ErrorNotice error={error} /> : null}
      </div>
    </ConfirmDialog>
  );
}
